import { Stack } from '@mui/material';
import { compact } from 'lodash';

import { HeaderCard } from '@/components/layout/HeaderCard';
import { Loader } from '@/components/progress/Loader/Loader';
import { useReportError } from '@/hooks/useReportError';

import {
  useAdminConfigurationsPageQuery,
  useAdminConfigurationsPageTenantQuery,
} from './graphql/AdminConfigurationsPage.generated';
import { SCIMSection } from './sections/SCIMSection';
import { SnowflakeSection } from './sections/SnowflakeSection';
import { SSOSetupSection } from './sections/SSOSetupSection';
import { ToggleSettingsSection } from './sections/ToggleSettingsSection';
import { WorkOSClientIDsSection } from './sections/WorkOSClientIDsSection';
import { TenantConfigurationForm } from './TenantConfigurationForm/TenantConfigurationForm';
import { TenantPresentationSettingsForm } from './TenantConfigurationForm/TenantPresentationSettingsForm';

export function AdminConfigurationsPage() {
  const { data, error, loading } = useAdminConfigurationsPageQuery();

  const tenantId = data?.tenantInformation?.id;
  const {
    data: tenantData,
    error: tenantError,
    loading: tenantLoading,
  } = useAdminConfigurationsPageTenantQuery({
    variables: { id: tenantId! },
    skip: !tenantId,
  });

  useReportError('failed to load configurations on admin page', error);
  useReportError('failed to load tenant details on admin page', tenantError);

  if (loading || tenantLoading) {
    return <Loader />;
  }

  const tenantInfo = data?.tenantInformation;
  const integrationConfig = data?.integrationConfiguration;
  const securityConfig = data?.tenantSecurityConfiguration;
  const ssoConnections = compact(
    data?.tenantSSOConnections?.edges?.map((edge) => edge?.node)
  );
  // The node query returns a union type; narrow to Tenant
  const tenant =
    tenantData?.node?.__typename === 'Tenant' ? tenantData.node : null;
  const isTenantDetailsLoaded = tenantData?.node?.__typename === 'Tenant';
  const hasExistingWorkosDirectoryID = Boolean(tenant?.workosDirectoryID);
  const isOrgMember = Boolean(tenant?.luminaryOrganization);

  return (
    <Stack spacing={2}>
      <HeaderCard heading="Tenant configuration">
        <TenantConfigurationForm isOrgMember={isOrgMember} />
      </HeaderCard>
      {/* Member tenants get presentation settings from the organization. */}
      {!isOrgMember && (
        <HeaderCard heading="Presentations">
          <TenantPresentationSettingsForm />
        </HeaderCard>
      )}
      <HeaderCard heading="Feature toggles">
        <ToggleSettingsSection
          integrationConfigurationId={integrationConfig?.id}
          enableLlmByok={integrationConfig?.enableLlmByok ?? false}
          tenantSecurityConfigurationId={securityConfig?.id}
          luminaryAccessControlEnabled={
            securityConfig?.luminaryAccessControlEnabled ?? false
          }
        />
      </HeaderCard>
      <HeaderCard heading="SSO setup">
        <SSOSetupSection
          workosOrganizationId={tenantInfo?.workosOrganizationId}
          existingSSOConnections={ssoConnections}
        />
      </HeaderCard>
      <HeaderCard heading="SCIM">
        <SCIMSection
          tenantId={tenantInfo?.id ?? ''}
          existingWorkosDirectoryID={tenant?.workosDirectoryID ?? null}
          hasExistingWorkosDirectoryID={hasExistingWorkosDirectoryID}
          isTenantDetailsLoaded={isTenantDetailsLoaded}
        />
      </HeaderCard>
      <HeaderCard heading="WorkOS client IDs">
        <WorkOSClientIDsSection
          tenantId={tenantInfo?.id ?? ''}
          existingWorkosOauthClientID={tenant?.workosOauthClientID ?? null}
          existingWorkosM2mClientID={tenant?.workosM2mClientID ?? null}
          isTenantDetailsLoaded={isTenantDetailsLoaded}
        />
      </HeaderCard>
      <HeaderCard heading="Snowflake">
        <SnowflakeSection
          integrationConfigurationId={integrationConfig?.id}
          currentValues={{
            snowflakeDataSharingDatabase:
              integrationConfig?.snowflakeDataSharingDatabase,
            snowflakeDataSharingRole:
              integrationConfig?.snowflakeDataSharingRole,
            snowflakeIntegrationDisplayName:
              integrationConfig?.snowflakeIntegrationDisplayName,
          }}
        />
      </HeaderCard>
    </Stack>
  );
}
